import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GradientText, GradientTextAction, InputLabel, Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import styled, { useTheme } from 'styled-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ConfirmDeleteModal } from 'src/components/modals';
import { TabWrap } from 'src/components/TabsMenu';
import { useAuth } from 'src/state/auth';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import { gql, useMutation } from '@apollo/client';
import {
  OrganisationQuery,
  UpsertOrganisationMutationVariables,
  useArchiveOrganisationMutation,
  useProfileSettingsOptionsQuery,
  useUpsertOrganisationMutation,
} from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';
import { OrganisationTypeSelect, BusinessTypeSelect, OrganisationSelect } from 'src/components/select';
import { getDiff } from 'src/utils/getDiff';
import logoSvg from 'src/assets/cydea.svg';
import { CheckIcon } from 'src/assets/dynamicSvg';
import { normalizeHexColor } from 'src/utils/color';
import { useImageLightness } from 'src/hooks/useImageLightness';

const Div = styled(TabWrap)`
  background-color: ${({ theme }) => theme.colors.white};
  position: relative;

  .logo-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .logo-current {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;

    &__details {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    &__image {
      width: 180px;
      height: 64px;
      object-fit: contain;
      background-color: ${({ theme }) => theme.colors.white};
      border: 1px solid ${({ theme }) => theme.colors.stroke};
      border-radius: 6px;
      padding: 6px 10px;
    }

    &__placeholder {
      width: 200px;
      height: 107px;
      border-radius: 8px;
      border: 1px solid ${({ theme }) => theme.colors.stroke};
      background-color: ${({ theme }) => theme.colors.cultured};
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__placeholder-icon {
      width: 28px;
      height: 31px;
      color: #6a7590;
    }
  }

  .branding-toast {
    position: absolute;
    top: 18px;
    right: 18px;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
    border-radius: 8px;
    padding: 12px 16px;
    max-width: 340px;
    z-index: 5;
  }

  .branding-toast__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .branding-toast__title {
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.prussianBlue};
  }

  .branding-toast__text {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.cflowerBlue};
    line-height: 1.4;
  }

  .logo-shadow {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3)) drop-shadow(0 0 1px rgba(0, 0, 0, 0.25));
  }

  .logo-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .logo-colors {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    position: relative;
  }

  .brand-preview {
    --preview-page-width: 340px;
    --preview-page-height: calc(var(--preview-page-width) * 1.4142);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: nowrap;
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    z-index: 20;
    width: max-content;
    max-width: calc(100vw - 80px);
    overflow-x: auto;
    padding-bottom: 6px;

    &__pdf,
    &__email {
      border-radius: 10px;
      border: 1px solid #e8e5ef;
      background-color: #ffffff;
      overflow: hidden;
      box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
    }

    &__pdf {
      width: calc(var(--preview-page-width) + 28px);
      height: calc(var(--preview-page-height) + 70px);
      display: flex;
      flex-direction: column;
      background-color: #f4f6fb;
    }

    &__pdf-scroll {
      padding: 12px 14px 18px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      overflow-y: auto;
      height: 100%;
      align-items: center;
    }

    &__pdf-page {
      width: var(--preview-page-width);
      aspect-ratio: 210 / 297;
      border-radius: 2px;
      background-color: #ffffff;
      border: 1px solid #e8e5ef;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
      flex: 0 0 auto;
    }

    &__pdf-banner {
      padding: 10px 14px 12px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__pdf-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    &__pdf-spacer {
      height: 8px;
    }

    &__pdf-logo {
      width: 62px;
      height: 22px;
      object-fit: contain;
    }

    &__pdf-company {
      font-size: 8.5px;
      font-weight: 600;
      color: #022033;
      text-align: right;
    }

    &__pdf-title {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-top: 4px;
    }

    &__pdf-label {
      font-size: 8px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    &__pdf-heading {
      font-size: 12px;
      font-weight: 600;
      color: #022033;
    }

    &__pdf-body {
      padding: 12px 14px 12px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
      justify-content: flex-end;
    }

    &__pdf-line {
      height: 5px;
      border-radius: 4px;
      background-color: #e8e5ef;
    }

    &__pdf-line--wide {
      width: 100%;
    }

    &__pdf-line--short {
      width: 70%;
    }

    &__pdf-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr)) 1.2fr;
      gap: 8px 10px;
    }

    &__pdf-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    &__pdf-footer {
      height: 18px;
      border-top: 1px solid #e8e5ef;
      background-color: #f4f6fb;
    }

    &__toc-header {
      padding: 12px 16px;
      border-bottom: 1px solid #e8e5ef;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    &__toc-body {
      display: flex;
      gap: 12px;
      padding: 12px 16px 14px;
      flex: 1;
    }

    &__toc-indicator {
      width: 6px;
      background-color: #f4f6fb;
      border-radius: 6px;
      overflow: hidden;
    }

    &__toc-indicator-fill {
      width: 100%;
      height: 24px;
      border-radius: 6px;
    }

    &__toc-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    &__toc-title {
      font-size: 12px;
      font-weight: 600;
      color: #022033;
    }

    &__toc-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
      flex: 1;
    }

    &__toc-item {
      display: grid;
      grid-template-columns: 28px 1fr 22px;
      align-items: center;
      gap: 6px;
      font-size: 9px;
      color: #022033;
    }

    &__toc-item-line {
      border-bottom: 1px dashed #e8e5ef;
      height: 1px;
      width: 100%;
      margin-top: 6px;
    }

    &__email {
      width: calc(var(--preview-page-width) + 28px);
      height: calc(var(--preview-page-height) + 70px);
      display: flex;
      flex-direction: column;
      background-color: #f0f4fb;
      border-radius: 10px;
      border: 1px solid #e8e5ef;
      overflow: hidden;
    }

    &__email-header {
      padding: 20px 12px 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    &__email-logo {
      width: 36px;
      height: 36px;
      object-fit: contain;
    }

    &__email-brand {
      font-size: 13px;
      font-weight: 700;
      color: #022033;
    }

    &__email-body {
      padding: 0 16px 20px;
      display: flex;
      flex-direction: column;
      flex: 1;
      align-items: center;
    }

    &__email-card {
      width: 100%;
      max-width: 220px;
      background-color: #ffffff;
      border: 1px solid #e8e5ef;
      border-radius: 2px;
      box-shadow:
        0 2px 0 rgba(0, 0, 150, 0.025),
        2px 4px 0 rgba(0, 0, 150, 0.015);
      padding: 18px 20px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    &__email-text {
      font-size: 11px;
      color: #022033;
      line-height: 1.5;
    }

    &__email-heading {
      font-size: 11px;
      font-weight: 700;
      color: #022033;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    &__email-link {
      font-size: 11px;
      color: #3869d4;
      text-decoration: underline;
      font-weight: 500;
    }

    &__email-alert {
      font-size: 11px;
      color: #e53e3e;
      font-weight: 600;
    }

    &__email-signoff {
      font-size: 11px;
      color: #022033;
    }
  }

  .logo-primary {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    position: relative;

    &__row {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    &__preview {
      position: relative;
      display: inline-flex;
      align-items: center;
    }

    &__swatches {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__swatch {
      width: 22px;
      height: 22px;
      border-radius: 4px;
      border: 1px solid ${({ theme }) => theme.colors.stroke};
      background-color: transparent;
      box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.white};
    }

    &__swatch--empty,
    &__input-swatch--empty {
      background-color: ${({ theme }) => theme.colors.white};
      background-image: linear-gradient(
          45deg,
          ${({ theme }) => theme.colors.stroke} 25%,
          transparent 25%,
          transparent 75%,
          ${({ theme }) => theme.colors.stroke} 75%,
          ${({ theme }) => theme.colors.stroke}
        ),
        linear-gradient(
          45deg,
          ${({ theme }) => theme.colors.stroke} 25%,
          transparent 25%,
          transparent 75%,
          ${({ theme }) => theme.colors.stroke} 75%,
          ${({ theme }) => theme.colors.stroke}
        );
      background-size: 6px 6px;
      background-position:
        0 0,
        3px 3px;
      background-repeat: repeat;
      background-origin: border-box;
      background-clip: border-box;
    }

    &__editor {
      width: 360px;
      border-radius: 12px;
      border: 1px solid ${({ theme }) => theme.colors.stroke};
      background-color: ${({ theme }) => theme.colors.white};
      box-shadow: 0 14px 32px rgba(15, 23, 42, 0.08);
      display: flex;
      flex-direction: column;
      gap: 16px;
      position: absolute;
      top: 42px;
      left: 0;
      z-index: 20;
    }

    &__editor-head {
      padding: 16px 18px 12px;
      border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
    }

    &__editor-body {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 0 18px;
    }

    &__section {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__input-row {
      display: flex;
      align-items: center;
      gap: 12px;
      background-color: ${({ theme }) => theme.colors.cultured};
      border: 1px solid ${({ theme }) => theme.colors.stroke};
      border-radius: 8px;
      padding: 10px 12px;
    }

    &__input-label {
      font-size: 12px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.cflowerBlue};
      letter-spacing: 0.2px;
    }

    &__input {
      flex: 1;
      border: none;
      font-size: 14px;
      font-family: Poppins, sans-serif;
      color: ${({ theme }) => theme.colors.prussianBlue};
      background-color: transparent;
      outline: none;
      text-align: right;
    }

    &__input-swatch {
      width: 18px;
      height: 18px;
      border-radius: 4px;
      border: 1px solid ${({ theme }) => theme.colors.stroke};
      background-color: transparent;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 0 18px 18px;
      justify-content: space-between;
      width: 100%;
    }

    &__action {
      border-radius: 6px;
      padding: 8px 16px;
      min-width: 92px;
      text-align: center;
      font-size: 14px;
      font-weight: 600;
      font-family: Poppins, sans-serif;
      cursor: pointer;
      border: 1px solid transparent;
    }

    &__action--ghost {
      background-color: transparent;
      color: ${({ theme }) => theme.colors.error};
      border-color: ${({ theme }) => theme.colors.stroke};
    }

    &__action--primary {
      background: ${({ theme }) => theme.colors.gradient};
      color: ${({ theme }) => theme.colors.white};
    }

    &__action--reset {
      background-color: ${({ theme }) => theme.colors.white};
      color: ${({ theme }) => theme.colors.error};
      border-color: ${({ theme }) => theme.colors.stroke};
    }

    &__action--disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  form {
    .contents {
      display: grid;
      grid-column-gap: 50px;
      grid-row-gap: 20px;
      grid-template-columns: 1fr 1fr;
    }

    .controls {
      display: flex;
      align-items: center;
      justify-content: space-between;
      grid-gap: 13px;
      gap: 13px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .logo-current {
      flex-direction: column;
      align-items: flex-start;
    }

    form {
      .contents {
        grid-template-columns: 1fr;
        grid-gap: 20px;
      }
    }
  }
`;

interface DetailsProps {
  organisation: NonNullable<OrganisationQuery['organisation']>;
}

export const Details: React.FC<DetailsProps> = ({ organisation }) => {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isRemovingLogo, setIsRemovingLogo] = useState(false);
  const [showBrandColorsEditor, setShowBrandColorsEditor] = useState(false);
  const [showBrandPreview, setShowBrandPreview] = useState(false);
  const [primaryColorDraft, setPrimaryColorDraft] = useState('');
  const [primaryColorError, setPrimaryColorError] = useState<string | null>(null);
  const [isUpdatingColors, setIsUpdatingColors] = useState(false);
  const [secondaryColorDraft, setSecondaryColorDraft] = useState('');
  const [secondaryColorError, setSecondaryColorError] = useState<string | null>(null);
  const [brandingSuccessMessage, setBrandingSuccessMessage] = useState<{ title: string; text: string } | null>(null);
  const [archiveWarningModalOpen, setArchiveWarningModalOpen] = useState(false);
  const { flatOrganisations } = useAuth();
  const { data: settingsData } = useProfileSettingsOptionsQuery();
  const theme = useTheme();
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const brandingSuccessTimeoutRef = useRef<number | null>(null);

  const { refetchQuery } = useCache();
  const maxLogoSizeBytes = 100 * 1024;
  const canEditSettings = organisation.actionsOnThisOrganisation?.canEditSettings;

  const validationSchema = useMemo(() => {
    const originalName = organisation.name?.trim().toLowerCase() || '';
    const existingOrgNames =
      flatOrganisations
        ?.filter((o) => o.recordType === 'O')
        .map((o) => o.name.trim().toLowerCase())
        .filter((name) => name !== originalName) || [];

    return Yup.object({
      name: Yup.string()
        .required('Required')
        .test('unique-name', 'Organisation name already exists', (value) => {
          if (!value) return true;
          return !existingOrgNames.includes(value.trim().toLowerCase());
        }),
      primaryContact: Yup.string().email('Invalid email address').required('Required'),
      billingContact: Yup.string().email('Invalid email address').nullable(),
    });
  }, [flatOrganisations, organisation.name]);
  const refetch = () => refetchQuery({ include: ['organisation', 'flatOrganisations', 'flatOrganisationsNav'] });

  const [update, { error }] = useUpsertOrganisationMutation();
  const [requestLogoUpload] = useMutation(
    gql`
      mutation requestLogoUpload($contentType: String!, $extension: String!) {
        requestLogoUpload(contentType: $contentType, extension: $extension) {
          uploadUrl
          key
          publicUrl
        }
      }
    `,
  );

  const [archiveOrganisation, { loading: isArchiving }] = useArchiveOrganisationMutation({
    onCompleted: () => refetch(),
  });

  const getLogoExtension = (file: File) => {
    const nameExtension = file.name.split('.').pop()?.toLowerCase() || '';
    if (nameExtension) return nameExtension;
    if (file.type === 'image/png') return 'png';
    if (file.type === 'image/jpeg' || file.type === 'image/jpg') return 'jpg';
    return '';
  };

  const applyLogoFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setLogoFile(file);
      setLogoPreviewUrl(reader.result as string);
      setLogoError(null);
    };
    reader.onerror = () => {
      setLogoError('Unable to read logo file.');
    };
    reader.readAsDataURL(file);
  };

  const clearLogoSelection = () => {
    setLogoFile(null);
    setLogoPreviewUrl(null);
  };

  const handleOpenBrandColorsEditor = () => {
    if (!canEditSettings) return;
    setPrimaryColorDraft(organisation.primaryColor || '');
    setPrimaryColorError(null);
    setSecondaryColorDraft(organisation.secondaryColor || '');
    setSecondaryColorError(null);
    setShowBrandColorsEditor(true);
  };

  const handleCloseBrandColorsEditor = () => {
    setShowBrandColorsEditor(false);
    setSecondaryColorDraft(organisation.secondaryColor || '');
    setSecondaryColorError(null);
    setPrimaryColorDraft(organisation.primaryColor || '');
    setPrimaryColorError(null);
  };

  const handleResetBrandColorsEditor = () => {
    setPrimaryColorDraft('');
    setSecondaryColorDraft('');
    setPrimaryColorError(null);
    setSecondaryColorError(null);
  };

  const handleLogoPicker = () => {
    if (!canEditSettings) return;
    setLogoError(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
      logoInputRef.current.click();
    }
  };

  const handleLogoInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > maxLogoSizeBytes) {
      setLogoError('Logo must be smaller than 100 KB.');
      clearLogoSelection();
      return;
    }

    const extension = getLogoExtension(file);
    if (!extension) {
      setLogoError('Only PNG or JPEG files are supported.');
      clearLogoSelection();
      return;
    }

    applyLogoFile(file);
  };

  const showBrandingSuccess = (title: string, text: string) => {
    setBrandingSuccessMessage({ title, text });
    if (brandingSuccessTimeoutRef.current) {
      window.clearTimeout(brandingSuccessTimeoutRef.current);
    }
    brandingSuccessTimeoutRef.current = window.setTimeout(() => {
      setBrandingSuccessMessage(null);
    }, 5000);
  };

  useEffect(() => {
    if (!showBrandColorsEditor) {
      setPrimaryColorDraft(organisation.primaryColor || '');
      setPrimaryColorError(null);
      setSecondaryColorDraft(organisation.secondaryColor || '');
      setSecondaryColorError(null);
    }
  }, [organisation.primaryColor, organisation.secondaryColor, showBrandColorsEditor]);

  useEffect(
    () => () => {
      if (brandingSuccessTimeoutRef.current) {
        window.clearTimeout(brandingSuccessTimeoutRef.current);
      }
    },
    [],
  );

  const currentLogoIsLight = useImageLightness(organisation.logoUrl);
  const previewLogoIsLight = useImageLightness(logoPreviewUrl);

  const initialValues = {
    name: organisation.name,
    primaryContact: organisation.primaryContact,
    billingContact: organisation.billingContact,
    domain: organisation.domain,
    organisationType: organisation.organisationType,
    businessType: organisation.businessType,
    parentId: organisation.parentId,
  } satisfies UpsertOrganisationMutationVariables as UpsertOrganisationMutationVariables;

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    handleSubmit,
    isValid,
    isSubmitting,
    dirty,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    validationSchema,
    initialValues,
    onSubmit: async (values) => {
      setLogoError(null);
      const diff = getDiff(initialValues, values);
      const payload = { organisationId: organisation.id, ...diff } as UpsertOrganisationMutationVariables;

      const pendingLogoFile = logoFile;
      if (pendingLogoFile) {
        if (pendingLogoFile.size > maxLogoSizeBytes) {
          setLogoError('Logo must be smaller than 100 KB.');
          return;
        }

        const extension = getLogoExtension(pendingLogoFile);
        if (!extension) {
          setLogoError('Unsupported file type.');
          return;
        }

        const contentType = pendingLogoFile.type || (extension === 'png' ? 'image/png' : 'image/jpeg');
        setIsUploadingLogo(true);
        try {
          const { data } = await requestLogoUpload({
            variables: {
              contentType,
              extension,
            },
          });

          const uploadUrl = data?.requestLogoUpload?.uploadUrl;
          const logoKey = data?.requestLogoUpload?.key;
          if (!uploadUrl || !logoKey) {
            setLogoError('Unable to request logo upload.');
            return;
          }

          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': contentType,
            },
            body: pendingLogoFile,
          });

          if (!uploadResponse.ok) {
            setLogoError('Logo upload failed. Please try again.');
            return;
          }

          payload.logoKey = logoKey;
        } catch (uploadError) {
          setLogoError('Logo upload failed. Please try again.');
          return;
        } finally {
          setIsUploadingLogo(false);
        }
      }

      await update({ variables: payload });
      await refetch();
      clearLogoSelection();
      if (payload.logoKey) {
        showBrandingSuccess('Logo updated successfully!', 'Enter your workspace to see the changes made.');
      }
    },
    enableReinitialize: true,
  });

  const handleBrandColorsUpdate = async () => {
    if (!canEditSettings || isUpdatingColors) return;
    setPrimaryColorError(null);
    setSecondaryColorError(null);

    const primaryTrimmed = primaryColorDraft.trim();
    let normalizedPrimary: string | null = null;
    if (primaryTrimmed) {
      normalizedPrimary = normalizeHexColor(primaryTrimmed);
      if (!normalizedPrimary) {
        setPrimaryColorError('Enter a valid hex code (e.g. #1A2B3C).');
        return;
      }
    }

    const secondaryTrimmed = secondaryColorDraft.trim();
    let normalizedSecondary: string | null = null;
    if (secondaryTrimmed) {
      normalizedSecondary = normalizeHexColor(secondaryTrimmed);
      if (!normalizedSecondary) {
        setSecondaryColorError('Enter a valid hex code (e.g. #1A2B3C).');
        return;
      }
    }

    setIsUpdatingColors(true);
    try {
      await update({
        variables: {
          organisationId: organisation.id,
          primaryColor: normalizedPrimary,
          secondaryColor: normalizedSecondary,
        },
      });
      await refetch();
      setShowBrandColorsEditor(false);
      showBrandingSuccess('Brand colours updated successfully!', 'Enter your workspace to see the changes made.');
    } catch (colorError) {
      setPrimaryColorError('Unable to update brand colours. Please try again.');
    } finally {
      setIsUpdatingColors(false);
    }
  };

  const selectedSettingsOption = settingsData?.profile?.orgSettingsOptions?.find(
    (o) => o.organisationType === values.organisationType,
  );
  const isPartnerOrg = organisation.organisationType === 'PARTNER';
  const hasLogoUpdate = Boolean(logoFile);
  const canSubmit = (dirty ? isValid : true) && (dirty || hasLogoUpdate) && !isSubmitting && !isUploadingLogo;
  const hasOrganisationLogo = Boolean(organisation.logoUrl);
  const hasLogoPreview = Boolean(logoPreviewUrl);
  const displayLogoUrl = logoPreviewUrl || organisation.logoUrl || logoSvg;
  const currentPrimaryColor = normalizeHexColor(organisation.primaryColor);
  const currentSecondaryColor = normalizeHexColor(organisation.secondaryColor);
  const canRemoveLogo = hasOrganisationLogo && canEditSettings && !isRemovingLogo;
  const canUpdateBrandColors = Boolean(canEditSettings) && !isUpdatingColors;
  const previewLogoSrc = logoPreviewUrl;
  const poweredLogoSrc = previewLogoSrc || displayLogoUrl;
  const previewPrimaryColor =
    normalizeHexColor(showBrandColorsEditor ? primaryColorDraft : organisation.primaryColor) ||
    currentPrimaryColor ||
    theme.colors.gp1;
  const previewSecondaryColor =
    normalizeHexColor(showBrandColorsEditor ? secondaryColorDraft : organisation.secondaryColor) ||
    currentSecondaryColor ||
    theme.colors.gp1;
  const currentLogoShadow = hasLogoPreview
    ? Boolean(previewLogoIsLight ?? true)
    : Boolean(organisation.logoUrl) && (currentLogoIsLight ?? true);
  const previewLogoShadow = Boolean(logoPreviewUrl) && (previewLogoIsLight ?? true);
  const poweredLogoShadow = logoPreviewUrl ? previewLogoShadow : currentLogoShadow;

  const availableOrganisationTypes = settingsData?.profile?.orgSettingsOptions
    ?.map((el) => el.organisationType)
    .filter((el) => el !== null && el !== undefined);

  return (
    <>
      {archiveWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={() => {
              archiveOrganisation({ variables: { id: organisation.id } });
              setArchiveWarningModalOpen(false);
            }}
            onClose={() => setArchiveWarningModalOpen(false)}
            confirmText="ARCHIVE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to archive{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {organisation.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      <Div>
        {brandingSuccessMessage && (
          <div className="branding-toast">
            <CheckIcon color="success" />
            <div className="branding-toast__content">
              <div className="branding-toast__title">{brandingSuccessMessage.title}</div>
              <div className="branding-toast__text">{brandingSuccessMessage.text}</div>
            </div>
          </div>
        )}
        <GradientText>DETAILS</GradientText>
        <Spacer $px={32} />
        {isPartnerOrg && (
          <div className="logo-section">
            <InputLabel>PARTNER LOGO</InputLabel>
            <div className="logo-current">
              <div className="logo-current__details">
                {hasOrganisationLogo || hasLogoPreview ? (
                  <img
                    className={['logo-current__image', currentLogoShadow && 'logo-shadow'].filter(Boolean).join(' ')}
                    src={displayLogoUrl}
                    alt="Current organisation logo"
                  />
                ) : (
                  <div className="logo-current__placeholder" aria-hidden>
                    <svg
                      className="logo-current__placeholder-icon"
                      viewBox="0 0 28 31"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M26.3984 29.0625H25.1875V1.45312C25.1875 0.650576 24.5369 0 23.7344 0H3.39062C2.58808 0 1.9375 0.650576 1.9375 1.45312V29.0625H0.726562C0.325318 29.0625 0 29.3878 0 29.7891V31H27.125V29.7891C27.125 29.3878 26.7997 29.0625 26.3984 29.0625ZM7.75 4.60156C7.75 4.20032 8.07532 3.875 8.47656 3.875H10.8984C11.2997 3.875 11.625 4.20032 11.625 4.60156V7.02344C11.625 7.42468 11.2997 7.75 10.8984 7.75H8.47656C8.07532 7.75 7.75 7.42468 7.75 7.02344V4.60156ZM7.75 10.4141C7.75 10.0128 8.07532 9.6875 8.47656 9.6875H10.8984C11.2997 9.6875 11.625 10.0128 11.625 10.4141V12.8359C11.625 13.2372 11.2997 13.5625 10.8984 13.5625H8.47656C8.07532 13.5625 7.75 13.2372 7.75 12.8359V10.4141ZM10.8984 19.375H8.47656C8.07532 19.375 7.75 19.0497 7.75 18.6484V16.2266C7.75 15.8253 8.07532 15.5 8.47656 15.5H10.8984C11.2997 15.5 11.625 15.8253 11.625 16.2266V18.6484C11.625 19.0497 11.2997 19.375 10.8984 19.375ZM15.5 29.0625H11.625V23.9766C11.625 23.5753 11.9503 23.25 12.3516 23.25H14.7734C15.1747 23.25 15.5 23.5753 15.5 23.9766V29.0625ZM19.375 18.6484C19.375 19.0497 19.0497 19.375 18.6484 19.375H16.2266C15.8253 19.375 15.5 19.0497 15.5 18.6484V16.2266C15.5 15.8253 15.8253 15.5 16.2266 15.5H18.6484C19.0497 15.5 19.375 15.8253 19.375 16.2266V18.6484ZM19.375 12.8359C19.375 13.2372 19.0497 13.5625 18.6484 13.5625H16.2266C15.8253 13.5625 15.5 13.2372 15.5 12.8359V10.4141C15.5 10.0128 15.8253 9.6875 16.2266 9.6875H18.6484C19.0497 9.6875 19.375 10.0128 19.375 10.4141V12.8359ZM19.375 7.02344C19.375 7.42468 19.0497 7.75 18.6484 7.75H16.2266C15.8253 7.75 15.5 7.42468 15.5 7.02344V4.60156C15.5 4.20032 15.8253 3.875 16.2266 3.875H18.6484C19.0497 3.875 19.375 4.20032 19.375 4.60156V7.02344Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                )}
                <div className="logo-actions">
                  <GradientTextAction
                    $underline
                    $disabled={!canEditSettings}
                    px={14}
                    weight={500}
                    onClick={handleLogoPicker}
                  >
                    {hasOrganisationLogo ? 'Update' : 'Upload logo'}
                  </GradientTextAction>
                  {hasOrganisationLogo && (
                    <GradientTextAction
                      $underline
                      $disabled={!canRemoveLogo}
                      px={14}
                      weight={500}
                      onClick={async () => {
                        if (!canRemoveLogo) return;
                        setLogoError(null);
                        setIsRemovingLogo(true);
                        try {
                          await update({ variables: { organisationId: organisation.id, logoKey: null } });
                          await refetch();
                          clearLogoSelection();
                          showBrandingSuccess(
                            'Logo removed successfully!',
                            'Enter your workspace to see the changes made.',
                          );
                        } catch (removeError) {
                          setLogoError('Unable to remove logo. Please try again.');
                        } finally {
                          setIsRemovingLogo(false);
                        }
                      }}
                    >
                      Remove
                    </GradientTextAction>
                  )}
                </div>
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                style={{ display: 'none' }}
                onChange={handleLogoInputChange}
              />
              <div className="logo-colors">
                <div className="logo-primary">
                  <div className="logo-primary__row">
                    <div className="logo-primary__swatches">
                      <span
                        className={['logo-primary__swatch', !currentPrimaryColor && 'logo-primary__swatch--empty']
                          .filter(Boolean)
                          .join(' ')}
                        style={currentPrimaryColor ? { backgroundColor: currentPrimaryColor } : undefined}
                      />
                      <span
                        className={['logo-primary__swatch', !currentSecondaryColor && 'logo-primary__swatch--empty']
                          .filter(Boolean)
                          .join(' ')}
                        style={currentSecondaryColor ? { backgroundColor: currentSecondaryColor } : undefined}
                      />
                    </div>
                    <GradientTextAction
                      $underline
                      $disabled={!canEditSettings}
                      px={14}
                      weight={500}
                      onClick={handleOpenBrandColorsEditor}
                    >
                      Update brand colours
                    </GradientTextAction>
                    <div className="logo-primary__preview">
                      <GradientTextAction
                        $underline
                        $disabled={!canEditSettings}
                        px={14}
                        weight={500}
                        onClick={() => {
                          if (!canEditSettings) return;
                          setShowBrandPreview((prev) => !prev);
                        }}
                      >
                        Preview PDF & email
                      </GradientTextAction>
                      {showBrandPreview && (
                        <div className="brand-preview">
                          <div className="brand-preview__pdf">
                            <div className="brand-preview__pdf-scroll">
                              <div className="brand-preview__pdf-page">
                                <div
                                  className="brand-preview__pdf-banner"
                                  style={{ backgroundColor: previewPrimaryColor }}
                                >
                                  <div className="brand-preview__pdf-top">
                                    <img
                                      className={['brand-preview__pdf-logo', poweredLogoShadow && 'logo-shadow']
                                        .filter(Boolean)
                                        .join(' ')}
                                      src={poweredLogoSrc}
                                      alt="PDF logo preview"
                                    />
                                    <span className="brand-preview__pdf-company">{organisation.name}</span>
                                  </div>
                                  <div className="brand-preview__pdf-spacer" />
                                  <div className="brand-preview__pdf-title">
                                    <span className="brand-preview__pdf-label" style={{ color: previewSecondaryColor }}>
                                      Risk assessment
                                    </span>
                                    <span className="brand-preview__pdf-heading">{organisation.name} assessment</span>
                                  </div>
                                </div>
                                <div className="brand-preview__pdf-body">
                                  <span className="brand-preview__pdf-label" style={{ color: previewSecondaryColor }}>
                                    Description
                                  </span>
                                  <div className="brand-preview__pdf-line brand-preview__pdf-line--wide" />
                                  <div className="brand-preview__pdf-line brand-preview__pdf-line--wide" />
                                  <div className="brand-preview__pdf-grid">
                                    {['Report date', 'Start date', 'Edited', 'Updated', 'Exported by'].map((label) => (
                                      <div key={label} className="brand-preview__pdf-field">
                                        <span
                                          className="brand-preview__pdf-label"
                                          style={{ color: previewSecondaryColor }}
                                        >
                                          {label}
                                        </span>
                                        <div className="brand-preview__pdf-line brand-preview__pdf-line--short" />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="brand-preview__pdf-footer" />
                              </div>
                              <div className="brand-preview__pdf-page">
                                <div className="brand-preview__toc-header">
                                  <img
                                    className={['brand-preview__pdf-logo', poweredLogoShadow && 'logo-shadow']
                                      .filter(Boolean)
                                      .join(' ')}
                                    src={poweredLogoSrc}
                                    alt="PDF logo preview"
                                  />
                                  <span className="brand-preview__pdf-company">Back to top</span>
                                </div>
                                <div className="brand-preview__toc-body">
                                  <div className="brand-preview__toc-indicator">
                                    <div
                                      className="brand-preview__toc-indicator-fill"
                                      style={{ backgroundColor: previewPrimaryColor }}
                                    />
                                  </div>
                                  <div className="brand-preview__toc-content">
                                    <div className="brand-preview__toc-title">TABLE OF CONTENTS</div>
                                    <div className="brand-preview__toc-list">
                                      {[
                                        { title: 'Executive summary', page: '01' },
                                        { title: 'Assessment summary', page: '02' },
                                        { title: 'Scenarios', page: '03' },
                                        { title: 'Risk management', page: '04' },
                                        { title: 'Incidents', page: '05' },
                                      ].map((item) => (
                                        <div key={item.title} className="brand-preview__toc-item">
                                          <span style={{ fontWeight: 600 }}>{item.page}</span>
                                          <div>
                                            <span>{item.title}</span>
                                            <div className="brand-preview__toc-item-line" />
                                          </div>
                                          <span style={{ textAlign: 'right' }}>{item.page}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                <div className="brand-preview__pdf-footer" />
                              </div>
                            </div>
                          </div>
                          <div className="brand-preview__email">
                            <div className="brand-preview__email-header">
                              <img
                                className={['brand-preview__email-logo', poweredLogoShadow && 'logo-shadow']
                                  .filter(Boolean)
                                  .join(' ')}
                                src={poweredLogoSrc}
                                alt="Email logo preview"
                              />
                              <span className="brand-preview__email-brand">{organisation.name}</span>
                            </div>
                            <div className="brand-preview__email-body">
                              <div className="brand-preview__email-card">
                                <div className="brand-preview__email-text">Hi {organisation.name},</div>
                                <div className="brand-preview__email-text">
                                  The following items are due for review in the next week:
                                </div>
                                <div className="brand-preview__email-heading">DUE TODAY</div>
                                <div className="brand-preview__email-text" style={{ fontWeight: 600 }}>
                                  Controls:
                                </div>
                                <div>
                                  <span className="brand-preview__email-link">Control 4</span>
                                  <span> – </span>
                                  <span className="brand-preview__email-alert">Today</span>
                                </div>
                                <div className="brand-preview__email-signoff">Regards,</div>
                                <div className="brand-preview__email-signoff">Cydea</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {showBrandColorsEditor && (
                    <div className="logo-primary__editor">
                      <div className="logo-primary__editor-head">
                        <Poppins px={16} weight={600} color="prussianBlue">
                          Brand colours
                        </Poppins>
                      </div>
                      <div className="logo-primary__editor-body">
                        <div className="logo-primary__section">
                          <Poppins px={12} weight={600} color="cflowerBlue">
                            Primary Colour
                          </Poppins>
                          <div className="logo-primary__input-row">
                            <span className="logo-primary__input-label">HEX Code:</span>
                            <input
                              className="logo-primary__input"
                              value={primaryColorDraft}
                              onChange={(event) => {
                                setPrimaryColorDraft(event.target.value);
                                if (primaryColorError) {
                                  setPrimaryColorError(null);
                                }
                              }}
                              placeholder="#1A2B3C"
                              maxLength={7}
                              disabled={!canEditSettings || isUpdatingColors}
                            />
                            <span
                              className={[
                                'logo-primary__input-swatch',
                                !(normalizeHexColor(primaryColorDraft) || currentPrimaryColor) &&
                                  'logo-primary__input-swatch--empty',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              style={{
                                backgroundColor:
                                  normalizeHexColor(primaryColorDraft) || currentPrimaryColor || undefined,
                              }}
                            />
                          </div>
                          {primaryColorError && (
                            <Poppins px={12} color="error">
                              {primaryColorError}
                            </Poppins>
                          )}
                        </div>
                        <div className="logo-primary__section">
                          <Poppins px={12} weight={600} color="cflowerBlue">
                            Secondary Colour
                          </Poppins>
                          <div className="logo-primary__input-row">
                            <span className="logo-primary__input-label">HEX Code:</span>
                            <input
                              className="logo-primary__input"
                              value={secondaryColorDraft}
                              onChange={(event) => {
                                setSecondaryColorDraft(event.target.value);
                                if (secondaryColorError) {
                                  setSecondaryColorError(null);
                                }
                              }}
                              placeholder="#1A2B3C"
                              maxLength={7}
                              disabled={!canEditSettings || isUpdatingColors}
                            />
                            <span
                              className={[
                                'logo-primary__input-swatch',
                                !(normalizeHexColor(secondaryColorDraft) || currentSecondaryColor) &&
                                  'logo-primary__input-swatch--empty',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              style={{
                                backgroundColor:
                                  normalizeHexColor(secondaryColorDraft) || currentSecondaryColor || undefined,
                              }}
                            />
                          </div>
                          {secondaryColorError && (
                            <Poppins px={12} color="error">
                              {secondaryColorError}
                            </Poppins>
                          )}
                        </div>
                      </div>
                      <div className="logo-primary__actions">
                        <button
                          type="button"
                          className="logo-primary__action logo-primary__action--ghost"
                          onClick={handleCloseBrandColorsEditor}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className={[
                            'logo-primary__action',
                            'logo-primary__action--reset',
                            isUpdatingColors && 'logo-primary__action--disabled',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={handleResetBrandColorsEditor}
                          disabled={isUpdatingColors}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className={[
                            'logo-primary__action',
                            'logo-primary__action--primary',
                            !canUpdateBrandColors && 'logo-primary__action--disabled',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={handleBrandColorsUpdate}
                          disabled={!canUpdateBrandColors}
                        >
                          Update
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {logoError && (
              <Poppins px={12} color="error">
                {logoError}
              </Poppins>
            )}
          </div>
        )}

        <Spacer $px={32} />
        <form onSubmit={handleSubmit}>
          <div className="contents">
            <div>
              <InputLabel>NAME *</InputLabel>
              <AutoGrowingInput
                id="name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
                fullWidth
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
            </div>

            <div>
              <InputLabel>ORGANISATION EMAIL DOMAIN</InputLabel>
              <AutoGrowingInput value={organisation.domain?.replace(/^@/, '')} fullWidth disabled />
            </div>

            <div>
              <InputLabel>PRIMARY CONTACT EMAIL ADDRESS *</InputLabel>
              <AutoGrowingInput
                id="primaryContact"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.primaryContact}
                type="email"
                fullWidth
                error={touched.primaryContact && Boolean(errors.primaryContact)}
                helperText={touched.primaryContact && errors.primaryContact}
              />
            </div>

            <div>
              <InputLabel>BILLING CONTACT EMAIL ADDRESS</InputLabel>
              <AutoGrowingInput
                id="billingContact"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.billingContact}
                type="email"
                fullWidth
                error={touched.billingContact && Boolean(errors.billingContact)}
                helperText={touched.billingContact && errors.billingContact}
              />
            </div>

            <div>
              <InputLabel tooltip="Changing the Organisation Type will automatically update and reset the related configuration settings below.">
                ORGANISATION TYPE
              </InputLabel>
              <OrganisationTypeSelect
                onChange={(o) => {
                  setFieldValue('organisationType', o.value);
                  setFieldValue('businessType', null);
                }}
                onBlur={() => setFieldTouched('organisationType', true)}
                value={values.organisationType}
                restrictedTo={availableOrganisationTypes}
                css="width: 100%;"
                error={touched.organisationType && Boolean(errors.organisationType)}
                disabled={!organisation.actionsOnThisOrganisation?.canEditOrganisationType}
              />
            </div>

            {selectedSettingsOption?.businessType ? (
              <div>
                <InputLabel>BUSINESS TYPE</InputLabel>
                <BusinessTypeSelect
                  restrictedTo={selectedSettingsOption?.businessType}
                  onChange={(o) => setFieldValue('businessType', o.value)}
                  onBlur={() => setFieldTouched('businessType', true)}
                  value={values.businessType}
                  css="width: 100%;"
                  error={touched.businessType && Boolean(errors.businessType)}
                />
              </div>
            ) : (
              <div />
            )}

            {organisation.actionsOnThisOrganisation?.canSeeParent && (
              <div>
                <InputLabel>PARENT ORGANISATION</InputLabel>
                <OrganisationSelect
                  value={values.parentId}
                  onChange={(o) => setFieldValue('parentId', o.value)}
                  onBlur={() => setFieldTouched('parentId', true)}
                  css="width: 100%;"
                  error={touched.parentId && Boolean(errors.parentId)}
                  disabled={!organisation.actionsOnThisOrganisation?.canEditParent}
                />
              </div>
            )}
          </div>

          {error && (
            <>
              <Spacer $px={20} />
              <div className="err">{error.message || 'Something went wrong'}</div>
            </>
          )}
          <Spacer $px={50} />
          <div className="controls">
            {canEditSettings && (
              <>
                <Button
                  primary
                  type="button"
                  disabled={!canSubmit}
                  $constWidth={220}
                  onClick={() => {
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Update',
                      tags: ['ORGANISATION'],
                    });
                    handleSubmit();
                  }}
                >
                  {isUploadingLogo ? 'UPLOADING...' : 'UPDATE'}
                </Button>
              </>
            )}
            {organisation.actionsOnThisOrganisation?.canArchive && !organisation.isArchived && (
              <Button
                secondary
                type="submit"
                disabled={isArchiving}
                $constWidth={220}
                onClick={() => setArchiveWarningModalOpen(true)}
              >
                ARCHIVE
              </Button>
            )}
          </div>
        </form>
      </Div>
    </>
  );
};
